<script lang="ts">
  // Shared choropleth canvas (projection, shapes, details) used by ResultsMap and ValueMap.
  // Details UX:
  //   mouse     → hover popover that follows the pointer, clamped inside the map
  //   touch/pen → a *tap* (not a scroll) opens a bottom sheet (portalled to <body>, above the replay bar);
  //               close with ×, Escape, a tap outside the sheet, or tapping the same shape again
  //   keyboard  → the map is ONE tab stop (a listbox): arrow keys move between shapes (popover at the shape),
  //               Home/End jump, Enter/Space opens the sheet, Escape closes it
  // `items` must keep its identity while the shape set is unchanged (geometry is re-projected only when it changes);
  // names/fills/labels come from functions so new numbers don't re-project anything.
  import type { Snippet } from 'svelte';
  import { geoMercator, geoPath } from 'd3-geo';
  import type { GeoFeature } from '../../lib/store';
  import { portal } from '../../lib/portal';

  export interface MapItem { id: string; f: GeoFeature }
  /** NYT-style shift arrow at a shape's centre: shift > 0 points left (toward the first side), < 0 right. */
  export interface MapArrow { shift: number; color: string; faint?: boolean }
  let {
    items, nameOf, fill, ariaFor, label, popup, maxHeight = 620, capToWidth = true, arrow = null, arrowFull = 25,
  }: {
    items: MapItem[];
    nameOf: (id: string) => string;
    fill: (id: string) => string;
    ariaFor: (id: string) => string;
    label: string;
    popup: Snippet<[string]>;
    maxHeight?: number;
    /** cap the height to ~1.1× the width (wide counties look better short) */
    capToWidth?: boolean;
    /** optional arrow per shape (shift maps); null = none */
    arrow?: ((id: string) => MapArrow | null) | null;
    /** |shift| (points) drawn at full arrow length */
    arrowFull?: number;
  } = $props();

  let width = $state(0);
  let box: HTMLDivElement | undefined = $state();
  let svgEl: SVGSVGElement | undefined = $state();
  let sheetEl: HTMLDivElement | undefined = $state();
  /** what is shown: a floating popover (mouse/keyboard) or the bottom sheet (touch / Enter) */
  let shown = $state<{ id: string; kind: 'pop' | 'sheet'; x: number; y: number } | null>(null);
  let tap: { id: string; x: number; y: number; t: number } | null = null;
  const POP_W = 240;
  // keyboard (roving active descendant)
  const uid = Math.random().toString(36).slice(2, 8);
  let active = $state<string | null>(null);
  const indexOf = $derived(new Map(items.map((it, i) => [it.id, i])));
  const domId = (id: string) => `mc-${uid}-${indexOf.get(id) ?? 0}`;

  const geom = $derived.by(() => {
    if (!width || !items.length) return { H: 0, paths: [] as { id: string; d: string; c: [number, number] }[] };
    const fc = { type: 'FeatureCollection' as const, features: items.map((i) => i.f) };
    const proj = geoMercator().fitWidth(width, fc);
    const [[, y0], [, y1]] = geoPath(proj).bounds(fc);
    let H = Math.ceil(y1 - y0);
    const cap = capToWidth ? Math.min(maxHeight, Math.max(280, width * 1.1)) : maxHeight;
    if (H > cap) H = cap;
    proj.fitSize([width, H], fc);
    const path = geoPath(proj);
    return { H, paths: items.map((i) => ({ id: i.id, d: path(i.f) ?? '', c: path.centroid(i.f) as [number, number] })) };
  });

  function popAt(id: string, x: number, y: number) { shown = { id, kind: 'pop', x, y }; }
  function local(e: { clientX: number; clientY: number }) {
    const r = box!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }
  function centerOf(el: SVGGraphicsElement) {
    const b = el.getBoundingClientRect();
    return local({ clientX: b.left + b.width / 2, clientY: b.top + b.height / 2 });
  }

  function onMove(id: string, e: PointerEvent) {
    if (e.pointerType !== 'mouse' || shown?.kind === 'sheet') return;
    const p = local(e);
    popAt(id, p.x, p.y);
  }
  function onDown(id: string, e: PointerEvent) {
    if (e.pointerType === 'mouse') return;
    tap = { id, x: e.clientX, y: e.clientY, t: e.timeStamp };
  }
  function onUp(id: string, e: PointerEvent) {
    if (e.pointerType === 'mouse' || !tap || tap.id !== id) return;
    const moved = Math.hypot(e.clientX - tap.x, e.clientY - tap.y);
    const quick = e.timeStamp - tap.t < 700;
    tap = null;
    if (moved > 10 || !quick) return;          // a scroll or a long press, not a tap
    shown = shown?.kind === 'sheet' && shown.id === id ? null : { id, kind: 'sheet', x: 0, y: 0 };
  }
  function showActive() {
    if (!active || !svgEl) return;
    const el = svgEl.querySelector<SVGPathElement>(`#${domId(active)}`);
    if (!el) return;
    const c = centerOf(el);
    popAt(active, c.x, c.y);
  }
  function onSvgFocus() {
    if (!svgEl?.matches(':focus-visible') || shown?.kind === 'sheet') return;   // pointer-initiated focus
    if (!active || !indexOf.has(active)) active = items[0]?.id ?? null;
    showActive();
  }
  function onSvgKey(e: KeyboardEvent) {
    if (!items.length) return;
    const i = active ? (indexOf.get(active) ?? 0) : -1;
    let j = i;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = Math.min(items.length - 1, i + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = Math.max(0, i - 1);
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = items.length - 1;
    else if ((e.key === 'Enter' || e.key === ' ') && active) { e.preventDefault(); shown = { id: active, kind: 'sheet', x: 0, y: 0 }; return; }
    else if (e.key === 'Escape') { close(); return; }
    else return;
    e.preventDefault();
    active = items[j]!.id;
    showActive();
  }
  function close() { shown = null; }

  // outside tap / Escape closes the sheet
  $effect(() => {
    if (shown?.kind !== 'sheet') return;
    const onDocDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (sheetEl?.contains(t) || svgEl?.contains(t)) return;   // taps on shapes are handled by onUp (toggle/switch)
      close();
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onDocDown, true);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('pointerdown', onDocDown, true); document.removeEventListener('keydown', onEsc); };
  });
  // the shape set changed (another race/mode/snapshot) and the shown unit no longer exists → close
  $effect(() => { if (shown && !items.some((i) => i.id === shown!.id)) shown = null; });

  // arrows: angled 35° up and toward the side the place moved; length ∝ |shift| up to arrowFull points; tiny shifts are
  // dots; smaller arrows drawn last so they stay visible inside clusters of big ones
  const ANG = (35 * Math.PI) / 180;
  const arrowsDrawn = $derived.by(() => {
    if (!arrow || !geom.H) return [];
    const maxLen = Math.max(22, Math.min(64, width * 0.075));
    const out: { id: string; d: string; head: string; color: string; w: number; dot: boolean; x: number; y: number; m: number; faint: boolean }[] = [];
    for (const p of geom.paths) {
      const a = arrow(p.id);
      const [x, y] = p.c;
      if (!a || !Number.isFinite(a.shift) || !Number.isFinite(x) || !Number.isFinite(y)) continue;
      const m = Math.abs(a.shift);
      const dir = a.shift > 0 ? -1 : 1;
      const faint = !!a.faint;
      if (m < 0.5) { out.push({ id: p.id, d: '', head: '', color: a.color, w: 1.4, dot: true, x, y, m, faint }); continue; }
      const len = Math.max(6, (Math.min(m, arrowFull) / arrowFull) * maxLen);
      const ex = x + dir * Math.cos(ANG) * len, ey = y - Math.sin(ANG) * len;
      const h = Math.min(6, 2.5 + len * 0.14);                     // arrowhead size
      const back = Math.atan2(ey - y, ex - x) + Math.PI;
      const hx = (t: number) => (ex + h * Math.cos(back + t)).toFixed(1), hy = (t: number) => (ey + h * Math.sin(back + t)).toFixed(1);
      out.push({ id: p.id, d: `M${x.toFixed(1)},${y.toFixed(1)}L${ex.toFixed(1)},${ey.toFixed(1)}`,
        head: `M${hx(0.5)},${hy(0.5)}L${ex.toFixed(1)},${ey.toFixed(1)}L${hx(-0.5)},${hy(-0.5)}`,
        color: a.color, w: 1.5 + Math.min(m, arrowFull) / arrowFull, dot: false, x, y, m, faint });
    }
    return out.sort((a, b) => b.m - a.m);
  });

  const popW = $derived(Math.min(POP_W, Math.max(160, width - 16)));
  const popLeft = $derived(shown ? Math.max(8, Math.min(shown.x + 14, width - popW - 8)) : 0);
  const popTop = $derived(shown ? Math.max(0, Math.min(shown.y - 30, geom.H - 40)) : 0);
  const shownName = $derived(shown ? nameOf(shown.id) : '');
</script>

<div class="box" bind:this={box} bind:clientWidth={width} role="presentation"
  onpointerleave={(e) => { if (e.pointerType === 'mouse' && shown?.kind === 'pop') close(); }}>
  {#if geom.H}
    <svg bind:this={svgEl} viewBox="0 0 {width} {geom.H}" width={width} height={geom.H} role="listbox" tabindex="0"
      aria-label="{label}. Use the arrow keys to move between areas; Enter for details."
      aria-activedescendant={active && indexOf.has(active) ? domId(active) : undefined}
      onfocus={onSvgFocus} onkeydown={onSvgKey} onblur={() => { if (shown?.kind === 'pop') close(); }}>
      {#each geom.paths as p (p.id)}
        <path id={domId(p.id)} d={p.d} style="fill:{fill(p.id)}" class:sel={shown?.id === p.id || active === p.id && shown !== null} role="option" tabindex="-1"
          aria-selected={active === p.id} aria-label={ariaFor(p.id)}
          onpointermove={(e) => onMove(p.id, e)} onpointerdown={(e) => onDown(p.id, e)} onpointerup={(e) => onUp(p.id, e)}
          onpointercancel={() => (tap = null)} />
      {/each}
      {#if arrowsDrawn.length}
        <g class="arrows" aria-hidden="true">
          {#each arrowsDrawn as a (a.id)}
            <g class:faint={a.faint}>
              {#if a.dot}
                <circle cx={a.x} cy={a.y} r="1.8" fill={a.color} />
              {:else}
                <path class="halo" d={a.d + a.head} style="stroke-width:{a.w + 2.4}px" />
                <path d={a.d + a.head} style="stroke:{a.color};stroke-width:{a.w}px" />
              {/if}
            </g>
          {/each}
        </g>
      {/if}
    </svg>
  {/if}
  {#if shown?.kind === 'pop'}
    <div class="pop" style="left:{popLeft}px;top:{popTop}px;width:{popW}px" role="status">{@render popup(shown.id)}</div>
  {/if}
</div>

{#if shown?.kind === 'sheet'}
  <div class="sheet" use:portal bind:this={sheetEl} role="dialog" aria-modal="false" aria-label={shownName}>
    <button class="close" type="button" onclick={close} aria-label="Close details">×</button>
    {@render popup(shown.id)}
  </div>
{/if}

<style>
  .box { position: relative; min-height: 120px; }
  svg { display: block; width: 100%; height: auto; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
  path { stroke: var(--map-stroke); stroke-width: 0.6; transition: fill var(--t-med) ease; cursor: pointer; outline: none; }
  path:hover, path.sel { stroke: var(--ink); stroke-width: 1.8; }
  .arrows { pointer-events: none; }
  .arrows path { fill: none; stroke-linecap: round; stroke-linejoin: round; cursor: default; transition: none; }
  .arrows path.halo { stroke: var(--card); opacity: 0.85; }
  .arrows .faint { opacity: 0.45; }
  svg:focus-visible { outline: 2px solid var(--focus); outline-offset: 4px; border-radius: 8px; }
  .pop { position: absolute; z-index: 5; background: var(--card); border: 1px solid var(--line); border-radius: 14px;
    box-shadow: var(--shadow-lg); padding: 12px 14px; pointer-events: none; font-size: 0.85rem; }
  .sheet { position: fixed; left: 10px; right: 10px; bottom: calc(var(--scrubber-h, 0px) + 14px + env(safe-area-inset-bottom, 0px));
    z-index: 70; max-width: 520px; margin: 0 auto; max-height: 55vh; overflow-y: auto; background: var(--card);
    border: 1px solid var(--line); border-radius: 18px; box-shadow: var(--shadow-lg); padding: 16px 18px; font-size: 0.9rem;
    animation: rise var(--t-med, 180ms) ease-out; }
  .close { position: absolute; right: 10px; top: 10px; border: 0; background: var(--card-3); color: var(--ink);
    width: 32px; height: 32px; border-radius: 50%; font-size: 1.15rem; line-height: 1; cursor: pointer; }
  @keyframes rise { from { transform: translateY(12px); opacity: 0; } to { transform: none; opacity: 1; } }
  @media (prefers-reduced-motion: reduce) { .sheet { animation: none; } path { transition: none; } }
</style>
